import React from 'react';
import {  PeriodType, DimensionsType, FindingDataItem, AiInsight } from '../context/AiInsightsApiTypesServices';
import { engagementLevels } from '../utils';

type Locale = 'en-EN' | 'fr-FR';

const tresholdVariation = 0.20;

interface SentenceGeneratorProps {
  insight: AiInsight;
  findingKey?: string;
  value?: FindingDataItem;
  bestSegment?: {
    historyDepth: number;
    mean: number;
    standardDeviation: number;
    referenceValueComparisonStats: {
      probabilityThreshold: number;
      trendConformity: string;
      variation: number;
      zScore: number;
    };
  };
}

export default function SentenceGenerator({ insight, value, bestSegment }: SentenceGeneratorProps) {
	const { translatedFindingName, findingType, period, dimensions } = insight;
	const locale =  getLocale();
	const periodDescription = mapPeriod(period, locale);
	const hasDays = dimensions && Object.keys(dimensions).includes('Days');
	const context = buildContext(dimensions, locale);

	let sentence = '';
	let comparedTo = '';
	if (bestSegment && value && value.history && value.history.segmentsStats && period !== 'LastDay') {
		const segmentsStats = value.history.segmentsStats;
		if (Array.isArray(segmentsStats) && segmentsStats.length > 0) {
			if (segmentsStats.length === 1 || bestSegment === segmentsStats[segmentsStats.length - 1]) {
				comparedTo = locale === 'fr-FR' ? ' par rapport à l’historique récent' : ' compared to recent history';
			} else if (bestSegment === segmentsStats[0]) {
				comparedTo = locale === 'fr-FR' ? ' par rapport à l’historique étendu' : ' compared to extended history';
			}
		}
	}

	function insertComparedToBeforeDot(str: string, comparedTo: string) {
		if (!comparedTo) return str;
		const lastDot = str.lastIndexOf('.');
		if (lastDot === -1) return str + comparedTo;
		return str.slice(0, lastDot) + comparedTo + str.slice(lastDot);
	}

	switch (findingType) {
		case 'BigUplift':
		case 'SmallUplift':
		case 'BigFall':
		case 'SmallFall': {
			const { variationName, variationIntensity } = getVariationDetailsFromBestSegment(bestSegment, locale);
			const base = `<strong>${translatedFindingName}</strong> ${context} ${variationName} ${variationIntensity} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'in') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
		case 'ProductPositiveSpotlight': {
			const base = `<strong>${translatedFindingName}</strong> ${locale === 'fr-FR' ? 'a une performance positive' : 'has a positive performance'} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'over') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
		case 'ProductNegativeSpotlight': {
			const base = `<strong>${translatedFindingName}</strong> ${locale === 'fr-FR' ? 'a une performance négative' : 'has a negative performance'} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'over') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
		case 'PositiveBusinessComparison': {
			const base = `<strong>${translatedFindingName}</strong> ${locale === 'fr-FR' ? 'est meilleur que le marché' : 'is better than the market'} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'over') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
		case 'NegativeBusinessComparison': {
			const base = `<strong>${translatedFindingName}</strong> ${locale === 'fr-FR' ? 'est moins bon que le marché' : 'is worse than the market'} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'over') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
		default: {
			const base = `<strong>${translatedFindingName}</strong> ${context} ${!hasDays ? (locale === 'fr-FR' ? 'sur' : 'over') + ' <strong>' + periodDescription + '</strong>.' : '<strong>' + periodDescription + '</strong>.'}`;
			sentence = insertComparedToBeforeDot(base, comparedTo);
			break;
		}
	}


	const legendDescription = getLegendDescription(locale, period, bestSegment?.historyDepth);

	return <p dangerouslySetInnerHTML={{__html: sentence + (legendDescription ? ' ' + legendDescription : '')}}></p>;
}

function getLocale(): Locale {
	const localeString = localStorage.getItem('i18nL');
	if (localeString && localeString.startsWith('fr-FR')) {
		return 'fr-FR';
	} else {
		return 'en-EN';
	}
}

function mapPeriod(period: PeriodType, locale: Locale): string {
	const periods = {
		'en-EN': {
			LastDay: 'last day',
			Last7Days: 'the last 7 days',
			Last30Days: 'the last 30 days',
			Last365Days: 'the last year',
		},
		'fr-FR': {
			LastDay: 'la veille',
			Last7Days: 'ces 7 derniers jours',
			Last30Days: 'ces 30 derniers jours',
			Last365Days: 'cette dernière année',
		},
	};

	return periods[locale][period] || '';
}

// function getVariationDetails(findingType: FindingType, locale: Locale) {

// 	const variations = {
// 		'en-EN': {
// 			BigUplift: { variationName: 'has increased', variationIntensity: 'significantly' },
// 			SmallUplift: { variationName: 'has increased', variationIntensity: '' },
// 			BigFall: { variationName: 'has decreased', variationIntensity: 'significantly' },
// 			SmallFall: { variationName: 'has decreased', variationIntensity: '' },
// 		},
// 		'fr-FR': {
// 			BigUplift: { variationName: 'est en hausse', variationIntensity: 'importante' },
// 			SmallUplift: { variationName: 'est en hausse', variationIntensity: '' },
// 			BigFall: { variationName: 'est en baisse', variationIntensity: 'importante' },
// 			SmallFall: { variationName: 'est en baisse', variationIntensity: '' },
// 		},
// 	};
	
// 	return variations[locale][findingType] || { variationName: '', variationIntensity: '' };
// }

function getVariationDetailsFromBestSegment(bestSegment: SentenceGeneratorProps['bestSegment'], locale: Locale) {
	if (!bestSegment) return { variationName: '', variationIntensity: '' };
	const variation = bestSegment.referenceValueComparisonStats.variation;
	const absVariation = Math.abs(variation);
	const isUplift = variation >= 0;
	const isBig = absVariation > tresholdVariation;

	if (locale === 'fr-FR') {
		if (isUplift && isBig) return { variationName: 'est en hausse', variationIntensity: 'importante' };
		if (isUplift && !isBig) return { variationName: 'est en hausse', variationIntensity: '' };
		if (!isUplift && isBig) return { variationName: 'est en baisse', variationIntensity: 'importante' };
		if (!isUplift && !isBig) return { variationName: 'est en baisse', variationIntensity: '' };
	} else {
		if (isUplift && isBig) return { variationName: 'has increased', variationIntensity: 'significantly' };
		if (isUplift && !isBig) return { variationName: 'has increased', variationIntensity: '' };
		if (!isUplift && isBig) return { variationName: 'has decreased', variationIntensity: 'significantly' };
		if (!isUplift && !isBig) return { variationName: 'has decreased', variationIntensity: '' };
	}
	return { variationName: '', variationIntensity: '' };
}

function buildContext(dimensions: DimensionsType, locale: Locale): string {
	const entries = Object.entries(dimensions) as [string, unknown][];
	// Place Days first if present
	const daysEntry = entries.find(([key]) => key === 'Days');
	const otherEntries = entries.filter(([key]) => key !== 'Days');
	const phrases: string[] = [];
	if (daysEntry) {
		phrases.push(mapDimension(daysEntry[0], daysEntry[1], locale));
	}
	phrases.push(...otherEntries.map(([key, value]) => mapDimension(key, value, locale)));
	return phrases.length > 0 ? phrases.join(', ') : '';
}

function mapDimension(key: string, value, locale: Locale): string {
	const isFrench = locale === 'fr-FR';

	switch (key) {
		case 'TrafficSource':
			return isFrench ? `pour la source de trafic <strong>${value}</strong>` : `for traffic source <strong>${value}</strong>`;
		case 'Device':
			return isFrench ? `sur les appareils <strong>${value}</strong>` : `on <strong>${value}</strong> devices`;
		case 'VisEngLevel':
		case 'FirstVisEngLevel':
		case 'LastVisEngLevel': {
			const engagementLabel = engagementLevels.find((level) => level.value === value)?.label || value;
			if (key === 'VisEngLevel') {
				return isFrench ? `avec l'engagement visiteur <strong>${engagementLabel}</strong>` : `with visitor engagement <strong>${engagementLabel}</strong>`;
			}
			if (key === 'FirstVisEngLevel') {
				return isFrench ? `avec le premier engagement visiteur <strong>${engagementLabel}</strong>` : `with first visitor engagement <strong>${engagementLabel}</strong>`;
			}
			if (key === 'LastVisEngLevel') {
				return isFrench ? `avec le dernier engagement visiteur <strong>${engagementLabel}</strong>` : `with last visitor engagement <strong>${engagementLabel}</strong>`;
			}
			break;
		}
		
		case 'PageType':
		case 'FirstPageType':
		case 'LastPageType':
			return mapPageType(value, locale);
		case 'Tenant':
			return isFrench ? `pour le tenant <strong>${value}</strong>` : `for tenant <strong>${value}</strong>`;
		case 'TransactionType':
			if (value === 'PromoCode') {
				return isFrench ? 'pour le type de transaction <strong>promo code</strong>' : 'for transaction type <strong>promo code</strong>';
			}
			return isFrench ? 'pour le type de transaction <strong>' + value + '</strong>' : 'for transaction type <strong>' + value + '</strong>';
		case 'Days':
			if (value === 'Weekend') {
				return isFrench ? 'pendant le <strong>week-end</strong>' : 'during the <strong>weekend</strong>';
			}
			return isFrench ? 'pendant <strong>' + value + '</strong>' : 'during <strong>' + value + '</strong>';
		case 'VisitorType':
			if (value === 'New') {
				return isFrench ? 'pour les <strong>nouveaux visiteurs</strong>' : 'for <strong>new visitors</strong>';
			}
			return isFrench ? 'pour le type de visiteur <strong>' + value + '</strong>' : 'for visitor type <strong>' + value + '</strong>';
		default:
			return `${key} ${value}`;
	}

	// Return a default value if none of the cases match
	return `${key} ${value}`;
}

const getLegendDescription = (locale,periodType,historyDepth) => {
	if (!historyDepth || !periodType) return null;
		
		
	const periodLabels = {
		'fr-FR': {
			'Last7Days': 'semaine',
			'Last30Days': 'mois',
			'Last90Days': 'trimestre',
			'Last365Days': 'année',
			'LastDay': 'jour'
		},
		'en-EN': {
			'Last7Days': 'week',
			'Last30Days': 'month',
			'Last90Days': 'quarter',
			'Last365Days': 'year',
			'LastDay': 'day'
		}
	};
		
	const periodLabel = periodLabels[locale][periodType] || (locale === 'fr-FR' ? 'période' : 'period');
	const isPlural = historyDepth > 1;
	const pluralSuffix = periodLabel === 'mois' ? '' : (isPlural ? 's' : '');
	const precedenteText = isPlural ? 'précédentes' : 'précédente';
		
	if (locale === 'fr-FR') {
		return `La période bleue représente la moyenne des ${historyDepth} ${periodLabel}${pluralSuffix} ${precedenteText}.`;
	} else {
		const pluralSuffix = isPlural ? 's' : '';
		const previousText = isPlural ? 'previous' : 'previous';
		return `The blue period represents the average of the ${historyDepth} ${previousText} ${periodLabel}${pluralSuffix}.`;
	}
};		

function mapPageType(value: string, locale: Locale): string {
	const pageTypeDescriptions = {
		H: { en: 'the home page', fr: 'la page d\'accueil' },
		FP: { en: 'the product page', fr: 'la page produit' },
		C: { en: 'the category page', fr: 'la page catégorie' },
		T: { en: 'the transaction page', fr: 'la page transaction' },
		G: { en: 'uncategorized pages', fr: 'les pages non catégorisées' },
		O: { en: 'uncategorized pages', fr: 'les pages non catégorisées' },
		B: { en: 'cart pages', fr: 'les pages panier' },
		BS: { en: 'cart summary page', fr: 'la page récapitulative du panier' },
		BL: { en: 'cart login page', fr: 'la page de connexion au panier' },
		BA: { en: 'cart address page', fr: 'la page d\'adresse du panier' },
		BC: { en: 'cart confirmation page', fr: 'la page de confirmation du panier' },
		BDevis: { en: 'quote cart page', fr: 'la page de devis du panier' },
		E: { en: 'error pages', fr: 'les pages d\'erreur' },
	};

	let pageDescription: { en: string; fr: string } | undefined;

	if (value.startsWith('B')) {
		pageDescription = { en: 'cart pages', fr: 'les pages panier' };
	} else {
		pageDescription = pageTypeDescriptions[value];
	}

	const description: string = pageDescription
		? locale === 'fr-FR'
			? pageDescription.fr
			: pageDescription.en
		: value;

	return locale === 'fr-FR' ? `pour ${description}` : `for ${description}`;
}
